/**
 * Quote service.
 *
 * Wraps the Railway routes that replaced the prior client-side
 * `quoteService` and the `extract-quote` Supabase edge function. The exported
 * surface mirrors the prior service 1:1 so call sites don't change.
 */

import { ApiError, apiPost } from "@/lib/apiClient";
import type {
  CreateResult,
  GetResult,
  ListResult,
  PaginatedResult,
  PaginationParams,
  ServiceContext,
  UpdateResult,
} from "@prolarus/shared";
import {
  toQueryString,
  tryCreate,
  tryGet,
  tryList,
  tryPaginated,
  tryUpdatePost,
} from "./_shared";

// ── Row shapes (preserved from the prior quoteService.ts) ────────────────

export interface QuoteListRow {
  quote_id: string | null;
  company_id: string | null;
  vendor_id: string | null;
  well_name: string | null;
  well_api: string | null;
  latest_version_id: string | null;
  vendor_quote_number: string | null;
  version_number: number | null;
  status_id: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  quote_modified: string | null;
  total_price: number | null;
  vendor: { name: string } | null;
  status_name: string | null;
}

export interface QuoteDetailRow {
  id: string;
  company_id: string;
  vendor_id: string;
  well_name: string | null;
  well_api: string | null;
  project_id: string | null;
  created: string;
  modified: string;
  vendor: { name: string };
  project: { name: string } | null;
}

export interface QuoteVersionRow {
  id: string;
  quote_id: string;
  company_id: string;
  version_number: number;
  status_id: string | null;
  vendor_quote_number: string | null;
  effective_date: string | null;
  expiration_date: string | null;
  vendor_notes: string | null;
  created: string;
  modified: string;
  status: { id: string; name: string } | null;
}

export interface QuoteItemRow {
  id: string;
  quote_version_id: string;
  company_id: string;
  sequence: number;
  vendor_itemcode: string | null;
  vendor_short_description: string | null;
  vendor_long_description: string | null;
  vendor_quantity: number | null;
  vendor_unit_price: number | null;
  vendor_price: number | null;
  vendor_uom: string | null;
}

// ── Extract response envelope ────────────────────────────────────────────

interface ExtractEnvelope {
  success: boolean;
  confidence?: number;
  data?: Record<string, unknown>;
  debug?: { processing_time_ms: number };
  error?: string;
}

export const quoteService = {
  // ── List variants ──────────────────────────────────────────────────────

  async listPaginated(
    _companyId: string,
    pagination: PaginationParams,
    filters?: { search?: string },
  ): Promise<PaginatedResult<QuoteListRow>> {
    const qs = toQueryString({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: filters?.search,
    });
    return tryPaginated<QuoteListRow>(`/api/quotes/paginated${qs}`);
  },

  async listByVendorPaginated(
    vendorId: string,
    pagination: PaginationParams,
    filters?: { search?: string },
  ): Promise<PaginatedResult<QuoteListRow>> {
    const qs = toQueryString({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: filters?.search,
    });
    return tryPaginated<QuoteListRow>(
      `/api/quotes/by-vendor/${encodeURIComponent(vendorId)}/paginated${qs}`,
    );
  },

  async listByProjectPaginated(
    projectId: string,
    pagination: PaginationParams,
    filters?: { search?: string },
  ): Promise<PaginatedResult<QuoteListRow>> {
    const qs = toQueryString({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: filters?.search,
    });
    return tryPaginated<QuoteListRow>(
      `/api/quotes/by-project/${encodeURIComponent(projectId)}/paginated${qs}`,
    );
  },

  async listUnassignedPaginated(
    _companyId: string,
    pagination: PaginationParams,
    filters?: { search?: string },
  ): Promise<PaginatedResult<QuoteListRow>> {
    const qs = toQueryString({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: filters?.search,
    });
    return tryPaginated<QuoteListRow>(`/api/quotes/unassigned/paginated${qs}`);
  },

  // ── Detail / version / item reads ──────────────────────────────────────

  getQuoteDetail(quoteId: string): Promise<GetResult<QuoteDetailRow>> {
    return tryGet<QuoteDetailRow>(`/api/quotes/${encodeURIComponent(quoteId)}`);
  },

  listVersionsByQuote(quoteId: string): Promise<ListResult<QuoteVersionRow>> {
    return tryList<QuoteVersionRow>(
      `/api/quotes/${encodeURIComponent(quoteId)}/versions`,
    );
  },

  listItemsByVersionPaginated(
    versionId: string,
    pagination: PaginationParams,
    filters?: { search?: string },
  ): Promise<PaginatedResult<QuoteItemRow>> {
    const qs = toQueryString({
      page: pagination.page,
      pageSize: pagination.pageSize,
      search: filters?.search,
    });
    return tryPaginated<QuoteItemRow>(
      `/api/quote-versions/${encodeURIComponent(versionId)}/items/paginated${qs}`,
    );
  },

  // ── Mutations ──────────────────────────────────────────────────────────

  async createQuote(
    payload: {
      company_id: string;
      vendor_id: string;
      well_name?: string | null;
      well_api?: string | null;
    },
    _context: ServiceContext,
  ): Promise<CreateResult<{ id: string }>> {
    // company_id is injected from JWT server-side; strip from payload.
    const { company_id: _company_id, ...rest } = payload;
    return tryCreate<{ id: string }>("/api/quotes", rest);
  },

  async createQuoteVersion(
    payload: {
      company_id: string;
      quote_id: string;
      version_number: number;
      status_id?: string | null;
      vendor_quote_number?: string | null;
      effective_date?: string | null;
      expiration_date?: string | null;
      vendor_notes?: string | null;
    },
    _context: ServiceContext,
  ): Promise<CreateResult<{ id: string }>> {
    const { company_id: _company_id, ...rest } = payload;
    return tryCreate<{ id: string }>("/api/quote-versions", rest);
  },

  async createQuoteItems(
    items: Array<{
      company_id: string;
      quote_version_id: string;
      sequence: number;
      vendor_itemcode?: string | null;
      vendor_short_description?: string | null;
      vendor_long_description?: string | null;
      vendor_quantity?: number | null;
      vendor_unit_price?: number | null;
      vendor_price?: number | null;
      vendor_uom?: string | null;
    }>,
    _context: ServiceContext,
  ): Promise<CreateResult<{ id: string }[]>> {
    // Strip client-supplied company_id; server injects from JWT.
    const stripped = items.map(({ company_id: _company_id, ...rest }) => rest);
    return tryCreate<{ id: string }[]>("/api/quote-items/batch", { items: stripped });
  },

  async assignToProject(
    quoteId: string,
    projectId: string,
  ): Promise<{ error: Error | null }> {
    try {
      await apiPost<{ success: boolean }>(
        `/api/quotes/${encodeURIComponent(quoteId)}/assign-project`,
        { project_id: projectId },
      );
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error(String(err)) };
    }
  },

  async unassignFromProject(quoteId: string): Promise<{ error: Error | null }> {
    try {
      await apiPost<{ success: boolean }>(
        `/api/quotes/${encodeURIComponent(quoteId)}/unassign-project`,
      );
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error(String(err)) };
    }
  },

  async deleteQuotes(quoteIds: string[]): Promise<{ error: Error | null }> {
    if (quoteIds.length === 0) return { error: null };
    try {
      await apiPost<{ success: boolean }>("/api/quotes/delete-batch", { ids: quoteIds });
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error(String(err)) };
    }
  },

  updateQuoteVersionStatus(
    versionId: string,
    newStatusId: string,
    _previousStatusId: string | null,
    _context: ServiceContext,
    options?: { comments?: string },
  ): Promise<UpdateResult<{ id: string }>> {
    return tryUpdatePost<{ id: string }>(
      `/api/quote-versions/${encodeURIComponent(versionId)}/status`,
      {
        to_status_id: newStatusId,
        comments: options?.comments ?? null,
      },
    );
  },

  // ── Extract ────────────────────────────────────────────────────────────

  async extractQuote(
    body: Record<string, string>,
  ): Promise<{ data: ExtractEnvelope | null; error: Error | null }> {
    try {
      const result = await apiPost<ExtractEnvelope>("/api/extract/quote", body);
      if (!result.success) {
        return { data: null, error: new Error(result.error || "Extraction failed") };
      }
      return { data: result, error: null };
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      return { data: null, error: new Error(message || "Failed to extract quote data") };
    }
  },
};
